import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import api from 'api';
import {
  DealRoomSearchResults,
  RWC_SEARCH_SECTION,
  RwcDealRoomSearchResults,
  SearchResultItem,
  SHARED_SECTION,
} from 'models/Search';
import useQueryParams from 'hooks/useQueryParams';
import AuthContext from 'providers/Auth/Auth.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import SearchContext from 'providers/Search/Search.context';
import useRecentSearches from 'providers/Search/hooks/useRecentSearches';
import SearchResultsTable from './components/SearchResultsTable';
import {
  ALL_DEALS,
  getSearchSectionTitle,
  groupBySection,
  isValidDealSection,
  mergeWithBestMatch,
} from './utils';
import { SearchFilters } from './components/SearchFilters';
import SearchEmptyState from './components/SearchEmptyState';
import SearchNoResults from './components/SearchNoResults';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { SearchInitiatedEventProps } from 'models/MixpanelEvents';
import { getArrayLength, getIsArrayEmpty, trimSearchString } from 'utils';
import RecentActivityPanel from './components/RecentActivityPanel';
import { MIN_SEARCH_QUERY_LENGTH } from 'data/global';
import { EMPTY_RWC_STATE, EMPTY_STATE } from './data';

import './Search.styles.scss';

const SearchPage: React.FC = () => {
  const {
    params: { searchString },
  } = useQueryParams();

  const trimmedSearchString = useMemo(
    () => trimSearchString(searchString),
    [searchString],
  );

  const isQueryTooShort = trimmedSearchString.length < MIN_SEARCH_QUERY_LENGTH;

  const [filter, setFilter] = useState(ALL_DEALS);

  const { popServerError } = useContext(PopNotificationsContext);
  const { isLoggedIn } = useContext(AuthContext);
  const { isSuperAdmin, isRWCMember } = useContext(CurrentUserContext);

  const [searchResults, setSearchResults] = useState<
    DealRoomSearchResults | RwcDealRoomSearchResults
  >(isRWCMember ? EMPTY_RWC_STATE : EMPTY_STATE);

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const {
    recentlyViewedDeals,
    isLoading: isLoadingRecentActivity,
    isSearching,
    addRecentSearch,
    setIsSearching,
  } = useContext(SearchContext);

  const recentSearches = useRecentSearches();

  const searchSections = useMemo(() => {
    if (isRWCMember)
      return Object.values(RWC_SEARCH_SECTION)
        .filter((section) => section !== SHARED_SECTION.BEST_MATCH)
        .map(
          (section) =>
            [section, (searchResults as RwcDealRoomSearchResults)[section]] as [
              string,
              SearchResultItem[],
            ],
        );

    return Object.entries(searchResults).filter(([key, value]) =>
      isValidDealSection(
        key,
        getArrayLength(value),
        ALL_DEALS,
        isLoggedIn,
        isSuperAdmin,
      ),
    );
  }, [isRWCMember, searchResults, isLoggedIn, isSuperAdmin]);

  const bestMatchDeals = useMemo(
    () => searchResults[SHARED_SECTION.BEST_MATCH] ?? [],
    [searchResults],
  );

  const bestMatchDealsBySection = useMemo(
    () => groupBySection(bestMatchDeals),
    [bestMatchDeals],
  );

  const filters = useMemo(() => {
    const visibleFilters = Object.fromEntries(
      searchSections.map(([key, value]) => [
        key,
        getArrayLength(mergeWithBestMatch(value, bestMatchDealsBySection[key])),
      ]),
    );

    return {
      [ALL_DEALS]: Object.values(visibleFilters).reduce(
        (acc, curr) => acc + curr,
        0,
      ),
      ...visibleFilters,
    };
  }, [searchSections, bestMatchDealsBySection]);

  const displayedDeals = useMemo(() => {
    const sections = searchSections.filter(
      ([key]) => filter === ALL_DEALS || filter === key,
    );

    if (getIsArrayEmpty(bestMatchDeals) || isSearching) return sections;

    if (filter !== ALL_DEALS) {
      return sections.map(([key, value]) => [
        key,
        mergeWithBestMatch(value, bestMatchDealsBySection[key]),
      ]) as [string, SearchResultItem[]][];
    }

    return [[SHARED_SECTION.BEST_MATCH, bestMatchDeals], ...sections] as [
      string,
      SearchResultItem[],
    ][];
  }, [
    searchSections,
    filter,
    bestMatchDeals,
    bestMatchDealsBySection,
    isSearching,
  ]);

  const hasNoResults = !isSearching && filters[ALL_DEALS] === 0;

  useEffect(() => {
    if (isQueryTooShort) {
      setSearchResults(isRWCMember ? EMPTY_RWC_STATE : EMPTY_STATE);
      setIsSearching(false);
      return;
    }

    let isStale = false;

    (async () => {
      try {
        setIsSearching(true);

        mixpanelService.searchInitiated(
          getMixpanelProps({
            searchedTerm: trimmedSearchString,
          }) as SearchInitiatedEventProps,
        );

        const { data } = await api.general[
          isLoggedIn ? 'searchDeals' : 'searchMarketPlace'
        ](trimmedSearchString);

        if (isStale) return;

        setSearchResults(data);
        addRecentSearch(trimmedSearchString);
      } catch (e) {
        if (!isStale) popServerError(e);
      } finally {
        if (!isStale) setIsSearching(false);
      }
    })();

    return () => {
      isStale = true;
    };
  }, [
    isLoggedIn,
    trimmedSearchString,
    isQueryTooShort,
    isRWCMember,
    popServerError,
    getMixpanelProps,
    addRecentSearch,
    setIsSearching,
  ]);

  return (
    <div className="search-page">
      <h1>
        {isQueryTooShort
          ? t('searchQueryTooShort', { count: MIN_SEARCH_QUERY_LENGTH })
          : t('searchResults', { searchString: trimmedSearchString })}
      </h1>

      {isQueryTooShort && isLoggedIn ? (
        <RecentActivityPanel
          isLoading={isLoadingRecentActivity}
          recentSearches={recentSearches}
          recentlyViewedDeals={recentlyViewedDeals}
        />
      ) : (
        <>
          {isLoggedIn && (
            <SearchFilters
              filters={filters}
              activeFilter={filter}
              setFilter={setFilter}
              isLoading={isSearching}
            />
          )}

          {hasNoResults ? (
            <SearchNoResults
              searchString={trimmedSearchString}
              recentSearches={recentSearches}
              recentlyViewedDeals={recentlyViewedDeals}
            />
          ) : (
            displayedDeals.map(([type, value]) => {
              const sectionTitle = t(getSearchSectionTitle(type, isSuperAdmin));

              return (
                <React.Fragment key={type}>
                  <h3>
                    {sectionTitle}
                    {!isSearching && ` (${getArrayLength(value)})`}
                  </h3>
                  <div
                    className={classNames(
                      'search-page__section',
                      'white-panel',
                    )}
                  >
                    {isSearching || getArrayLength(value) ? (
                      <SearchResultsTable
                        deals={value}
                        type={type}
                        searchString={trimmedSearchString}
                        isLoading={isSearching}
                      />
                    ) : filter === ALL_DEALS ? (
                      <p className="search-page__section-empty">
                        {t('noMatchesInSection', { section: sectionTitle })}
                      </p>
                    ) : (
                      <SearchEmptyState
                        title={t('noMatchesInSectionHeading', {
                          section: sectionTitle.toLowerCase(),
                        })}
                        description={t('resultsInOtherSections', {
                          count: filters[ALL_DEALS],
                        })}
                        action={{
                          label: t('viewAllResults'),
                          onClick: () => setFilter(ALL_DEALS),
                        }}
                      />
                    )}
                  </div>
                </React.Fragment>
              );
            })
          )}
        </>
      )}
    </div>
  );
};

export default SearchPage;
